import PropTypes from 'prop-types'
import { connect } from 'react-redux'

import DownloadIcon from '../../assets/DownloadIcon'
import LocationIcon from '../../assets/LocationIcon'
import {
  PAYMENT_MODE_OPTIONS,
  PRODUCT_DETAIL_FIELDS,
  DELIVERY_STATUS_DISPLAY,
  DELIVERY_TRACKING_STEPS,
  SALES_TYPE,
} from '../../common/constant/page-constant'
import { formatDate, formatTime } from '../../common/utils/appUtils'
import SideDrawer from '../Common/SideDrawer/SideDrawer'
import './Sales.scss'

import PhoneIcon from '../../assets/PhoneIcon'
import PrintIcon from '../../assets/PrintIcon'
import { useGeneratePDF } from '../../common/hooks/useGeneratePDF'
import currencyFormatter from './../../common/utils/currencyFormatterUtils'
import { formatProductListLabel } from './../../common/utils/productUtils'
import Config from '../../config/config'
import { formatInvoiceDate } from './../../common/utils/dateFormatter';
import { printInvoiceFromData, downloadInvoiceFromData, INVOICE_COPY_TYPE } from './printHandler'

function ViewSales({ open, handleDrawerToggle, data, loginData }) {
  const { ref, isPrinting, isDownloading, generatePDF } = useGeneratePDF()

  // Check if this is an online sale
  const isOnlineSale = data?.type === 'ONLINE'

  // Get current delivery status step for tracking
  const getCurrentDeliveryStep = () => {
    // If order is completed, return final step
    if (data?.status === SALES_TYPE.COMPLETED) {
      return DELIVERY_TRACKING_STEPS.length
    }
    
    const deliveryStatus = data?.delivery_partner?.status
    if (!deliveryStatus) return 0
    
    const stepIndex = DELIVERY_TRACKING_STEPS.indexOf(deliveryStatus)
    return stepIndex >= 0 ? stepIndex + 1 : 0
  }

  // Print the simplified customer copy (separate layout from the pharmacy copy).
  // Reuses the shared invoice template/print infra and the already-loaded order
  // data, so no extra API call is needed.
  const handlePrintCustomerCopy = () => {
    if (!data) return
    printInvoiceFromData(data, INVOICE_COPY_TYPE.CUSTOMER_COPY, loginData?.pharmacy_name)
  }

  // Download the customer copy as a PDF (matches the printed Customer Copy
  // layout, not the on-screen pharmacy copy template).
  const handleDownloadCustomerCopy = () => {
    if (!data) return
    downloadInvoiceFromData(data, INVOICE_COPY_TYPE.CUSTOMER_COPY, loginData?.pharmacy_name)
  }

  // VAT amount is now stored directly, not as percentage
  const getVatAmount = () => {
    return Number(data.vat || 0).toFixed(2)
  }

  const getVatPercentage = () => {
    if (data.vat_percentage !== undefined && data.vat_percentage !== null) {
      return data.vat_percentage
    }
    const baseAmount = (data.amount || 0) - getDiscountAmount() - getTradeDiscountAmount()
    if (baseAmount > 0 && data.vat > 0) {
      return parseFloat(((data.vat / baseAmount) * 100).toFixed(2))
    }
    return 0
  }

  const getDiscountAmount = () => Number(data.discount) || 0

  const getTradeDiscountAmount = () => {
    if (data.trade_discount && data.amount)
      return (data.trade_discount / 100) * data.amount
    return 0
  }

  const getPaymentModeLabel = () => {
    const mode = PAYMENT_MODE_OPTIONS.find((item) => item.name === data.payment_mode)
    return mode?.label || data?.payment_mode || 'Cash'
  }

  // THERMAL PRINTING FUNCTIONALITY (KEEP AS IS)

  const removePDFStyles = () => {
    const style = document.getElementById('pdf-font-styles')
    if (style) {
      style.remove()
    }
  }

  const removeDownloadStyles = () => {
    const style = document.getElementById('download-font-styles')
    if (style) {
      style.remove()
    }
  }

  const printPDF = () => {
    generatePDF(true)
  }

  return (
    <SideDrawer
      handleDrawerToggle={handleDrawerToggle}
      open={open}
      drawerHeading="Sales"
    >
      <div className="viewProductWrap">
        {(isDownloading || isPrinting) && (
          <div className="loader">
            <p>
              {isDownloading
                ? 'Please wait while we are generating your invoice...'
                : 'Preparing to print...'}
            </p>
          </div>
        )}

        {/* Order Tracking Stepper - Only for Online Orders (moved to top) */}
        {isOnlineSale && SALES_TYPE.CONFIRMED_LIST.includes(data?.status) && (
          <div className="orderTrackingSection">
            <div className="trackingHeader">
              <span className="trackingTitle">Order Tracking</span>
              {data?.delivery_partner?.name && (
                <span className="deliveryPersonInfo">
                  Delivery: {data.delivery_partner.name} 
                  {data.delivery_partner.mobile_no && ` (${data.delivery_partner.mobile_no})`}
                </span>
              )}
            </div>
            <div className="trackingStepper">
              {DELIVERY_TRACKING_STEPS.map((status, index) => {
                const stepInfo = DELIVERY_STATUS_DISPLAY[status]
                const currentStep = getCurrentDeliveryStep()
                const isCompleted = index < currentStep
                const isActive = index === currentStep - 1
                const isLast = index === DELIVERY_TRACKING_STEPS.length - 1
                
                return (
                  <div 
                    key={status} 
                    className={`trackingStep ${isCompleted ? 'completed' : ''} ${isActive ? 'active' : ''} ${isLast ? 'last' : ''}`}
                  >
                    <div className="stepContent">
                      <div className="stepCircle">
                        {isCompleted ? '✓' : index + 1}
                      </div>
                      <div className="stepLabel">{stepInfo.text}</div>
                    </div>
                    {!isLast && (
                      <div className={`stepLine ${isCompleted ? 'completed' : ''}`} />
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )}

        <div ref={ref} className="print pharmacyCopyTemplate">
          {/* Title */}
          <div className="invoiceTitle">SALES INVOICE</div>

          {/* Order Tracking for Print - Only for Online Orders */}
          {isOnlineSale && SALES_TYPE.CONFIRMED_LIST.includes(data?.status) && (
            <>
              <div className="printOrderTracking">
                <div className="printTrackingHeader">
                  <span className="printTrackingTitle">Order Tracking</span>
                  {data?.delivery_partner?.name && (
                    <span className="printDeliveryInfo">
                      Delivery: {data.delivery_partner.name} ({data.delivery_partner.mobile_no})
                    </span>
                  )}
                </div>
                <div className="printTrackingStepper">
                  {DELIVERY_TRACKING_STEPS.map((status, index) => {
                    const stepInfo = DELIVERY_STATUS_DISPLAY[status]
                    const currentStep = getCurrentDeliveryStep()
                    const isCompleted = index < currentStep
                    const isActive = index === currentStep - 1
                    const isLast = index === DELIVERY_TRACKING_STEPS.length - 1
                    
                    return (
                      <div 
                        key={status} 
                        className={`printStep ${isCompleted ? 'completed' : ''} ${isActive ? 'active' : ''}`}
                      >
                        <div className="printStepContent">
                          <div className="printStepCircle">
                            {isCompleted ? '✓' : index + 1}
                          </div>
                          <div className="printStepLabel">{stepInfo.text}</div>
                        </div>
                        {!isLast && <div className={`printStepLine ${isCompleted ? 'completed' : ''}`} />}
                      </div>
                    )
                  })}
                </div>
              </div>
              <br />
            </>
          )}

          {/* Branch Header Section */}
          <div className="branchHeaderSection">
            <div className="branchLogoRow">
              {data?.branch_details?.logo && (
                <img
                  src={Config.getFullImageUrl(data.branch_details.logo)}
                  alt="logo"
                  className="branchLogo"
                />
              )}
              <div className="branchInfoWrapper">
                <div className="branchInfoRow">
                  <span className="branchName">
                    {loginData?.pharmacy_name || data?.branch_details?.pharmacy_name || ''} 
                    {data?.branch_details?.name ? ` (${data?.branch_details?.name})` : ''}
                  </span>
                  <span className="separator">|</span>
                  <span className="branchPhone">{data?.branch_details?.mobile_no}</span>
                </div>
                <div className="branchAddressRow">
                  <LocationIcon className="icon" />
                  <span>{data?.branch_details?.address}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Invoice Info Section */}
          <div className="invoiceInfoSection">
            <div className="invoiceInfoRow">
              <div className="invoiceLeft">
                <span className="invoiceLabel">Invoice :</span>
                <span className="invoiceNumber">{data.invoice_number}</span>
                <span className={`invoiceTypeBadge ${isOnlineSale ? 'online' : 'instore'}`}>
                  {isOnlineSale ? 'Online' : 'In Store'}
                </span>
              </div>
              <div className="invoiceRight">
                <span>{formatDate(data.updated_at || data.created_at)}</span>
                <span className="invoiceTime">{formatTime(data.updated_at || data.created_at)}</span>
              </div>
            </div>
            <div className="servedByRow">
              <span className="servedByLabel">Served by :</span>
              <span className="servedByName">{data.served_by || data.created_by_name || loginData?.name || 'Staff'}</span>
            </div>
          </div>

          {/* Customer Section */}
          <div className="customerSection">
            {isOnlineSale ? (
              /* Online Sales - Show DG ePharma */
              <div className="customerInfoRow">
                <span className="customerName">DG ePharma</span>
                <span className="customerPhoneIcon">
                  <PhoneIcon />
                </span>
              </div>
            ) : (
              /* In-Store Sales - Show Customer Info */
              <>
                <div className="customerInfoRow">
                  <span className="customerName">{data?.customer?.name || 'Walk-in Customer'}</span>
                  <span className="separator">|</span>
                  <span className="customerPhone">{data?.customer?.mobile_no || '-'}</span>
                </div>
                <div className="customerAddressRow">
                  <LocationIcon className="icon" />
                  <span>{data?.customer?.shipping_address || data?.customer?.billing_address || '-'}</span>
                </div>
              </>
            )}
          </div>

          {/* Products Table */}
          <div className="productDescriptionWrap">
            <table className="invoiceTable pharmacyCopyTable">
              <thead>
                <tr>
                  <th width="5%">SI No.</th>
                  <th width="23%">Product Name (Size)-Strength</th>
                  <th width="11%">Category</th>
                  <th width="12%">Batch No.</th>
                  <th width="12%">Expiry Date</th>
                  <th width="10%" style={{ textAlign: 'right' }}>Cost/<br />Unit</th>
                  <th width="10%" style={{ textAlign: 'right' }}>Rate/<br />Unit</th>
                  <th width="7%" style={{ textAlign: 'center' }}>Qty.</th>
                  <th width="12%" style={{ textAlign: 'right' }}>Amount</th>
                </tr>
              </thead>

              <tbody>
                {data.product_details &&
                  data.product_details.map((product, index) => {
                    const qty = product?.[PRODUCT_DETAIL_FIELDS.QUANTITY] || 0
                    const rate = product?.[PRODUCT_DETAIL_FIELDS.RATE_PER_UNIT] || 0
                    const costPerUnit = product?.[PRODUCT_DETAIL_FIELDS.COST_PER_UNIT] || product?.cost_per_unit || 0
                    const amount = qty * rate
                    const productName = product?.[PRODUCT_DETAIL_FIELDS.PRODUCT_NAME] || ''
                    const dose = product?.[PRODUCT_DETAIL_FIELDS.DOSE] || ''
                    const strength = product?.strength || ''
                    const size = product?.size || ''
                    const category = product?.category || product?.product_category || ''
                    const lineLabel = formatProductListLabel(
                      {
                        name: productName,
                        size,
                        dose,
                        strength,
                        category,
                      },
                      { includeCategory: false }
                    )

                    return (
                      <tr key={index}>
                        <td>{index + 1}</td>
                        <td className="tdWrap">
                          {lineLabel}
                        </td>
                        <td>{category}</td>
                        <td>
                          {product?.[PRODUCT_DETAIL_FIELDS.INVENTORY]?.[PRODUCT_DETAIL_FIELDS.BATCH_NUMBER] || '-'}
                        </td>
                        <td>{formatDate(product?.inventory?.expiry_date) || '-'}</td>
                        <td style={{ textAlign: 'right' }}>
                          {currencyFormatter(Number(costPerUnit).toFixed(2))}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          {currencyFormatter(Number(rate).toFixed(2))}
                        </td>
                        <td style={{ textAlign: 'center' }}>{qty}</td>
                        <td style={{ textAlign: 'right' }}>
                          {currencyFormatter(Number(amount).toFixed(2))}
                        </td>
                      </tr>
                    )
                  })}
              </tbody>
            </table>

            {/* Summary Section */}
            <div className="summarySection">
              <div className="summaryRow">
                <span className="summaryLabel">Amount (BDT)</span>
                <span className="summaryValue">
                  {currencyFormatter(Number(data.amount || 0).toFixed(2))}
                </span>
              </div>
              <div className="summaryRow">
                <span className="summaryLabel">Discount ({data.discount || 0}%)</span>
                <span className="summaryValue">
                  {Number(getDiscountAmount() || 0).toFixed(2)}
                </span>
              </div>
              <div className="summaryRow">
                <span className="summaryLabel">VAT ({getVatPercentage()}%)</span>
                <span className="summaryValue">
                  {getVatAmount()}
                </span>
              </div>
              <div className="summaryRow totalRow">
                <span className="summaryLabel">Total (BDT)</span>
                <span className="summaryValue totalValue">
                  {currencyFormatter(Number(data.total_amount || 0).toFixed(2))}
                </span>
              </div>
              
              {/* Payment Mode - Same column alignment */}
              {data.payment_mode && (
                <>
                  <div className="summaryRow">
                    <span className="summaryLabel">Payment Mode</span>
                    <span className="summaryValue">{getPaymentModeLabel()}</span>
                  </div>
                  
                  {/* Cash Payment */}
                  {data.payment_mode === 'CASH' && (
                    <div className="summaryRow">
                      <span className="summaryLabel">Paid by Cash</span>
                      <span className="summaryValue">{Number(data.paid_amount || data.total_amount || 0).toFixed(2)}</span>
                    </div>
                  )}
                  
                  {/* Card Payment Details */}
                  {data.payment_mode === 'CARD' && (
                    <>
                      {data.transaction_fee_percent > 0 && (
                        <div className="summaryRow">
                          <span className="summaryLabel">Fee ({data.transaction_fee_percent}%)</span>
                          <span className="summaryValue">{Number(data.transaction_fee_amount || 0).toFixed(2)}</span>
                        </div>
                      )}
                      {data.final_card_amount && data.transaction_fee_amount > 0 && (
                        <div className="summaryRow">
                          <span className="summaryLabel">Final Card Amt</span>
                          <span className="summaryValue">{Number(data.final_card_amount || 0).toFixed(2)}</span>
                        </div>
                      )}
                      {data.total_payable && data.transaction_fee_amount > 0 && (
                        <div className="summaryRow" style={{ fontWeight: 'bold' }}>
                          <span className="summaryLabel">Total Payable</span>
                          <span className="summaryValue">{Number(data.total_payable || 0).toFixed(2)}</span>
                        </div>
                      )}
                    </>
                  )}
                  
                  {/* Online Payment Details */}
                  {data.payment_mode === 'ONLINE' && (
                    <>
                      {data.mfs_name && (
                        <div className="summaryRow">
                          <span className="summaryLabel">MFS</span>
                          <span className="summaryValue">{data.mfs_name}</span>
                        </div>
                      )}
                      {data.transaction_fee_percent > 0 && (
                        <div className="summaryRow">
                          <span className="summaryLabel">Fee ({data.transaction_fee_percent}%)</span>
                          <span className="summaryValue">{Number(data.transaction_fee_amount || 0).toFixed(2)}</span>
                        </div>
                      )}
                      {data.total_payable && data.transaction_fee_amount > 0 && (
                        <div className="summaryRow" style={{ fontWeight: 'bold' }}>
                          <span className="summaryLabel">Total Payable</span>
                          <span className="summaryValue">{Number(data.total_payable || 0).toFixed(2)}</span>
                        </div>
                      )}
                    </>
                  )}
                  
                  {/* Cash & Card Payment Details */}
                  {data.payment_mode === 'CASH_CARD' && (
                    <>
                      <div className="summaryRow">
                        <span className="summaryLabel">Paid by Cash</span>
                        <span className="summaryValue">{Number(data.amount_cash || 0).toFixed(2)}</span>
                      </div>
                      <div className="summaryRow">
                        <span className="summaryLabel">Paid by Card</span>
                        <span className="summaryValue">{Number(data.amount_card || 0).toFixed(2)}</span>
                      </div>
                      {data.transaction_fee_percent > 0 && (
                        <div className="summaryRow">
                          <span className="summaryLabel">Fee ({data.transaction_fee_percent}%)</span>
                          <span className="summaryValue">{Number(data.transaction_fee_amount || 0).toFixed(2)}</span>
                        </div>
                      )}
                      {data.final_card_amount && data.transaction_fee_amount > 0 && (
                        <div className="summaryRow">
                          <span className="summaryLabel">Final Card Amt</span>
                          <span className="summaryValue">{Number(data.final_card_amount || 0).toFixed(2)}</span>
                        </div>
                      )}
                      {data.total_payable && data.transaction_fee_amount > 0 && (
                        <div className="summaryRow" style={{ fontWeight: 'bold' }}>
                          <span className="summaryLabel">Total Payable</span>
                          <span className="summaryValue">{Number(data.total_payable || 0).toFixed(2)}</span>
                        </div>
                      )}
                    </>
                  )}
                  
                  {/* Due Sales */}
                  {data.payment_mode === 'DUE_SALES' && (
                    <div className="summaryRow">
                      <span className="summaryLabel">Due Amount</span>
                      <span className="summaryValue">{data.payment_status !== 'PENDING' ? '0.00' : Number(data.total_amount || 0).toFixed(2) }</span>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Notes Section */}
            <div className="notesSection">
              <span className="notesLabel">Notes:</span>
              <div className="notesContent"></div>
            </div>
              {data.payment_mode === 'DUE_SALES' && data.payment_status !== 'PENDING' && (
                <>
                  <div className="summaryRow">
                    <span className="summaryLabel">Payment Status:</span>
                    <span className="summaryValue">Received</span>
                  </div>
                  <div className="summaryRow">
                    <span className="summaryLabel">Received Date:</span>
                    <span className="summaryValue">{data.updated_at && formatInvoiceDate(data.updated_at)}</span>
                  </div>
                  <div className="summaryRow">
                    <span className="summaryLabel">Received Mode:</span>
                    <span className="summaryValue">{data.payment_status === 'PAID_VOUCHER' ? 'Cash in Hand' : ''}</span>
                  </div>
                </>
              )}
          </div>
          
        </div>

        <div className="actionButton mt-5 dFlex fdColumn ai-flex-end">
          <div className="download-print-button-group">
            <button
              className="download-print-button print"
              style={{
                cursor: isDownloading || isPrinting ? 'not-allowed' : 'pointer',
                opacity: isDownloading || isPrinting ? 0.6 : 1,
              }}
              onClick={() => generatePDF(true)}
              disabled={isDownloading || isPrinting}
            >
              <PrintIcon />
              <span className="printButtonMargin">Pharmacy Copy</span>
            </button>

            <button
              className="download-print-button print"
              style={{
                cursor: isDownloading || isPrinting ? 'not-allowed' : 'pointer',
                opacity: isDownloading || isPrinting ? 0.6 : 1,
              }}
              onClick={handlePrintCustomerCopy}
              disabled={isDownloading || isPrinting}
            >
              <PrintIcon />
              <span className="printButtonMargin">Customer Copy</span>
            </button>

            <button
              className="download-print-button download"
              style={{
                cursor: isDownloading || isPrinting ? 'not-allowed' : 'pointer',
                opacity: isDownloading || isPrinting ? 0.6 : 1,
              }}
              onClick={handleDownloadCustomerCopy}
              disabled={isDownloading || isPrinting}
            >
              <DownloadIcon />
              <span className="downloadButtonMargin">Download</span>
            </button>
          </div>
        </div>
      </div>
    </SideDrawer>
  )
}

const mapStateToProps = (state) => {
  return {
    loginData: state.loginDataReducer?.loginData,
  }
}

const mapDispatchToProps = (dispatch) => {
  return {}
}

ViewSales.propTypes = {
  open: PropTypes.bool,
  handleDrawerToggle: PropTypes.func,
  loginData: PropTypes.object,
}

export default connect(mapStateToProps, mapDispatchToProps)(ViewSales)
